import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Textarea } from '../../../shared/components/Textarea';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { VacationRequestItem } from '../api/ferias';
import { VACATION_DECISION_LABELS, formatDate } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useDecidirFerias, usePendentesFerias } from '../hooks/ferias';

type Etapa = 'gestor' | 'rh';

const DECISOES = [
  { value: 'aprovado', label: 'Aprovar' },
  { value: 'devolvido', label: 'Devolver para ajuste' },
  { value: 'rejeitado', label: 'Reprovar' },
];

/**
 * Aprovação de férias (US-GP-012): o gestor decide a etapa dos liderados
 * diretos; o RH, a etapa de RH. Os conflitos aparecem antes da decisão.
 */
export default function AprovacoesFeriasPage() {
  const { can } = usePermissions();
  const rh = can(FEATURES.pessoasFeriasAprovacao, 'edit');
  const gestor = can(FEATURES.meusColaboradores, 'view');
  const [etapa, setEtapa] = useState<Etapa>(gestor ? 'gestor' : 'rh');

  return (
    <PageContainer title="Aprovações de férias" breadcrumbs={[{ label: 'Pessoas' }, { label: 'Aprovações de férias' }]}>
      {gestor && rh && (
        <div className="mb-4 flex gap-2">
          <Button size="sm" variant={etapa === 'gestor' ? 'primary' : 'secondary'} onClick={() => setEtapa('gestor')}>Minha equipe</Button>
          <Button size="sm" variant={etapa === 'rh' ? 'primary' : 'secondary'} onClick={() => setEtapa('rh')}>Etapa do RH</Button>
        </div>
      )}
      <Pendentes key={etapa} etapa={etapa} />
    </PageContainer>
  );
}

function Pendentes({ etapa }: { etapa: Etapa }) {
  const toast = useToast();
  const pendentes = usePendentesFerias(etapa);
  const decidir = useDecidirFerias(etapa);
  const [item, setItem] = useState<VacationRequestItem | null>(null);
  const [decision, setDecision] = useState('aprovado');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!item) return;
    if (decision !== 'aprovado' && !comment.trim()) return setError('Informe o comentário.');
    try {
      const r = await decidir.mutateAsync({ id: item.id, decision, comment: comment.trim() || undefined });
      setItem(null);
      toast.success(r.status === 'aprovada' ? 'Férias aprovadas.' : r.currentStep === 'rh' ? 'Aprovado: segue para o RH.' : 'Decisão registrada.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar a decisão.'));
    }
  }

  const lista = pendentes.data ?? [];
  if (pendentes.isLoading) return <p className="text-sm text-gray-500">Carregando…</p>;
  return (
    <>
      {lista.length === 0 ? (
        <EmptyState title="Nenhuma solicitação aguardando você" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {lista.map((r) => (
            <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 text-sm">
              <div className="space-y-1">
                <p className="font-medium text-gray-900">
                  {etapa === 'rh'
                    ? <Link className="text-blue-700 hover:underline" to={`/pessoas/colaboradores/${r.userId}`}>{r.personName}</Link>
                    : <Link className="text-blue-700 hover:underline" to={`/meus-colaboradores/${r.userId}/ferias`}>{r.personName}</Link>}
                  {' '}· nº {r.number} · matrícula {r.internalRegistration}
                </p>
                <p className="text-gray-700">
                  {r.installments.map((i) => `${formatDate(i.startDate)} a ${formatDate(i.endDate)} (${i.days} d)`).join(' · ')}
                  {r.sellDays > 0 && ` · vende ${r.sellDays} dias`}{r.advance13th && ' · adianta 13º'}
                </p>
                <p className="text-xs text-gray-500">
                  Aquisitivo {formatDate(r.period.acquisitionStart)} a {formatDate(r.period.acquisitionEnd)} · concessivo até {formatDate(r.period.concessionEnd)}
                  {' · '}solicitado por {r.requestedByName ?? '—'}
                </p>
                {r.approvals.filter((a) => a.cycle === r.cycle).map((a, i) => (
                  <p key={i} className="text-xs text-gray-500">Gestor: {VACATION_DECISION_LABELS[a.decision]} por {a.decidedByName ?? '—'}</p>
                ))}
                {r.notes && <p className="text-xs text-gray-600">Observação: {r.notes}</p>}
                {(r.conflicts ?? []).length > 0 && (
                  <ul className="list-disc pl-5 text-xs text-amber-800">{r.conflicts!.map((c, i) => <li key={i}>{c.message}</li>)}</ul>
                )}
              </div>
              <Button size="sm" onClick={() => { setDecision('aprovado'); setComment(''); setError(null); setItem(r); }}>Decidir</Button>
            </li>
          ))}
        </ul>
      )}
      <PessoasModal open={item !== null} title={`Férias de ${item?.personName ?? ''}`} submitLabel="Registrar"
        submitVariant={decision === 'rejeitado' ? 'danger' : 'primary'} isSubmitting={decidir.isPending} submitError={error}
        onClose={() => setItem(null)} onSubmit={submit}>
        <AutocompleteSelect label="Decisão" options={DECISOES} value={decision} onChange={setDecision} />
        <Textarea label={decision === 'aprovado' ? 'Comentário (opcional)' : 'Comentário'} value={comment} onChange={(e) => setComment(e.target.value)} />
      </PessoasModal>
    </>
  );
}
